import { IconPlus, IconX } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import { ActionCenterTab, useActionCenter } from "~/contexts/actionCenterProvider";
import { ActionIcon, Button, Center, Flex, Text, Tooltip, tokens, useHotkeys } from "~/ui";
import { api } from "~/utils/api";

import { ActionCenterTasks } from "./ActionCenterTasks";
import styles from "./InboxPanel.module.css";
import { getInboxHeaderSummary } from "./inbox-header-counts";
import { inboxStyleVars } from "./inbox-style-vars";

export const ActionCenter = (): JSX.Element => {
  const actionCenterContext = useActionCenter();
  const countsQuery = api.inbox.counts.useQuery();
  const headerSummary = getInboxHeaderSummary(countsQuery.data?.unreadNotifications, countsQuery.data?.openTasks);
  // "New task" lives in the header but the create form lives in the list, so the flag is held here.
  // The panel body unmounts when the panel closes (it always has), so a closed panel forgets an
  // unfinished form — reset it on close to keep that.
  const [isCreating, setIsCreating] = useState(false);
  const isOpen = actionCenterContext.state.isOpen;

  useEffect(() => {
    if (!isOpen) {
      setIsCreating(false);
    }
  }, [isOpen]);

  useHotkeys([
    [
      "I",
      () => {
        actionCenterContext.setIsOpen(!actionCenterContext.state.isOpen);
      },
    ],
  ]);

  const handleClose = () => {
    actionCenterContext.setIsOpen(false);
  };

  const contentComponent = useMemo(() => {
    switch (actionCenterContext.state.activeTab) {
      case ActionCenterTab.TASKS:
        return <ActionCenterTasks isCreating={isCreating} onCreatingChange={setIsCreating} />;
      default:
        return (
          <Center h={150} w="100%">
            <Text>Select a Tab above to view contents</Text>
          </Center>
        );
    }
  }, [actionCenterContext.state, isCreating]);

  if (!isOpen) {
    return <></>;
  }
  return (
    <Flex className={styles.panel} direction="column" style={inboxStyleVars}>
      <div className={styles.header}>
        <div>
          <Text c={tokens.color.content.strong} fw={600} fz="lg" lh={tokens.typography.textLineHeight.lg}>
            Inbox
          </Text>
          {/* Content.secondary is the handoff's secondary text (no raw hex): muted (gray[6]) is ~3.4:1 on white,
              under WCAG AA for 12px text (handoff §7). No count line until inbox.counts has landed. */}
          {headerSummary && (
            <Text c={tokens.color.content.secondary} fz="sm" lh={tokens.typography.textLineHeight.sm}>
              {headerSummary.isCaughtUp && "You're all caught up"}
              {headerSummary.unreadLabel && (
                <Text component="span" c={tokens.color.action.informational} fw={500}>
                  {headerSummary.unreadLabel}
                </Text>
              )}
              {headerSummary.unreadLabel && headerSummary.openTasksLabel && " · "}
              {headerSummary.openTasksLabel}
            </Text>
          )}
        </div>
        <div className={styles.headerActions}>
          {!isCreating && (
            <Button leftIcon={<IconPlus size={14} />} radius="sm" size="xs" onClick={() => setIsCreating(true)}>
              New task
            </Button>
          )}
          <Tooltip label="Close (I)" position="bottom" withArrow>
            <ActionIcon aria-label="Close Inbox" className={styles.closeButton} radius="sm" size={30} onClick={handleClose}>
              <IconX size={18} />
            </ActionIcon>
          </Tooltip>
        </div>
      </div>
      {contentComponent}
      <div className={styles.hint}>
        <Text c={tokens.color.content.secondary} fz="xs" lh={tokens.typography.textLineHeight.xs}>
          Press <kbd className={styles.kbd}>I</kbd> to open or close
        </Text>
      </div>
    </Flex>
  );
};
